import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { client } from '../../lib/sanity';
import { getPetUrlIdentifier } from '../../lib/petUtils';
import type { Pet } from '../../types/sanity';
import { PetCard } from '../pets/PetCard';
import { LoadingMessage } from '../common/LoadingMessage';
import { ErrorMessage } from '../common/ErrorMessage';

export function VirtualAdoptionPets() {
  const [pets, setPets] = React.useState<Pet[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + 
      (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} mies.`;
    }
    
    const age = Math.floor(ageInMonths / 12);
    return `${age} ${age === 1 ? 'rok' : age < 5 ? 'lata' : 'lat'}`;
  };

  const handleMeetPet = (identifier: string) => {
    navigate(`/zwierze/${identifier}`);
  };

  React.useEffect(() => {
    const fetchPets = async () => {
      try {
        const result = await client.fetch<Pet[]>(`
          *[_type == "pet" && status != "adopted" && !(_id in path("drafts.**"))] | order(_createdAt desc)[0...6] {
            _id,
            name,
            slug,
            birthDate,
            image,
            description,
            gender,
            size,
            status,
            "tags": tags[]-> {
              _type,
              _ref,
              label,
              "slug": slug
            },
            mainCategory,
            gallery
          }
        `);

        setPets(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching pets:', err);
        setError('Nie udało się załadować zwierząt');
        setLoading(false);
      }
    };

    fetchPets();
  }, []);

  return (
    <div className="mt-16">
      <div className="text-center mb-12">
        <h2 className="section-title mb-4">
          Zwierzaki czekające na wirtualnych opiekunów
        </h2>
        <p className="body-large">
          Poznaj naszych podopiecznych, którzy potrzebują Twojego wsparcia
        </p>
      </div>

      {loading ? (
        <LoadingMessage ariaLabel="Ładowanie zwierząt do adopcji wirtualnej" />
      ) : error ? (
        <ErrorMessage message={error} ariaLabel="Błąd ładowania zwierząt do adopcji wirtualnej" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {pets.map((pet) => (
            <PetCard
              key={pet._id}
              pet={pet}
              onMeetPet={handleMeetPet}
              calculateAge={calculateAge}
            />
          ))}
        </div>
      )}

      <div className="text-center">
        <button
          onClick={() => navigate('/zwierzeta')}
          className="inline-flex items-center gap-2 bg-accessible-red-600 text-white px-6 py-3 rounded-lg hover:bg-accessible-red-700 transition-colors btn-text-medium"
          title="Zobacz wszystkie zwierzęta do adopcji wirtualnej"
        >
          <span>Zobacz wszystkie zwierzaki</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}